/**
 * Indicator Colour Utilities
 *
 * Small helpers shared by indicator definitions (which pick zone colours and
 * derive edge shades) and the canvas primitives that paint them (label outlines,
 * translucent band fills).
 *
 * Everything accepts `#rgb` / `#rrggbb`, `rgb()` / `rgba()` and passes through
 * anything it cannot parse, so a mis-typed setting never breaks rendering.
 */

interface Rgb { r: number; g: number; b: number }

const clamp255 = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

/** Parse `#rgb`, `#rrggbb`, `rgb()`/`rgba()` into 0–255 channels. */
export function hexToRgb(color: string): Rgb | null {
  if (!color) return null;
  const value = color.trim();

  if (value.startsWith("#")) {
    let hex = value.slice(1);
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6 || /[^0-9a-fA-F]/.test(hex)) return null;
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
    };
  }

  const fn = value.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (fn) {
    return { r: Number(fn[1]), g: Number(fn[2]), b: Number(fn[3]) };
  }

  return null;
}

/**
 * Re-colour a hex/rgb string with an alpha channel, e.g.
 * `withAlpha("#26A69A", 0.25)` → `"rgba(38, 166, 154, 0.25)"`.
 * Existing `rgba()` inputs have their alpha replaced; unparseable input is
 * returned unchanged so the browser can decide.
 */
export function withAlpha(color: string, alpha: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}

/**
 * Darken (factor < 1) or lighten (factor > 1) a colour.
 * Used to derive a darker outline for label boxes and band edges from the
 * zone's own fill colour.
 */
export function shade(color: string, factor: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const f = Math.max(0, factor);
  return `rgb(${clamp255(rgb.r * f)}, ${clamp255(rgb.g * f)}, ${clamp255(rgb.b * f)})`;
}

/** WCAG relative luminance → pick black or white text for a given background. */
export function contrastTextColor(color: string): string {
  const rgb = hexToRgb(color);
  if (!rgb) return "#FFFFFF";
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance > 0.62 ? "#10131A" : "#FFFFFF";
}